/* Expanse 6.1.0 + Bloomreach facets
  - Single-row scroller + double-bounce (CSS handles)
  - "All Sizes" first, pressed by default
  - Chip click toggles the NATIVE size checkbox (+ change event) → hydrates grid
  - URL ?size=... repeated keys kept via history.replaceState (no reload)
  - FAST "All Sizes": bulk uncheck + single change, then strip size params
  - Fallback: if pills still remain after 2 frames, drain them one by one
  - Excludes size values 4–12 from showing as chips
*/
(function () {
 'use strict';
 if (!/\/collections(\/|$)/.test(location.pathname)) return;
 if (window.__BR_SIZE_SELECTOR_INITIALIZED__) return;
 window.__BR_SIZE_SELECTOR_INITIALIZED__ = true;
 const URL_KEY_SIZE = 'size';
 const EXCLUDED_SIZES = new Set(['4','5','6','7','8','9','10','11','12']);
 let selected = new Set();
 let sizesCacheKey = '';
 let globalsBound = false;
 let SUPPRESS_SYNC = false;
 /* ---------- Facet helpers ---------- */
 function getSizeFacet(){
   const explicit = document.querySelector('.filters-content__group[data-facet="size"]');
   if (explicit) return explicit;
   const groups = document.querySelectorAll('.filters-content__group');
   for (const g of groups) {
     const t = g.querySelector('.filters-content__title');
     if (t && /^\s*size\s*$/i.test(t.textContent.trim())) return g;
   }
   return null;
 }
 function getAllSizeCheckboxes(){
   const facet = getSizeFacet();
   return facet ? Array.from(facet.querySelectorAll('input[type="checkbox"]')) : [];
 }
 function setCheckboxCheckedByValue(value, checked){
   const boxes = getAllSizeCheckboxes();
   for (const cb of boxes) {
     if ((cb.value || '').trim() === value) {
       if (cb.checked !== checked) {
         cb.checked = checked;
         cb.dispatchEvent(new Event('change', { bubbles: true })); // hydrates grid
       }
       break;
     }
   }
 }
 function readAvailableSizes(){
   const facet = getSizeFacet();
   if (!facet) return [];
   const seen = new Set();
   const out = [];
   facet.querySelectorAll('label').forEach(lab => {
     const cb = lab.querySelector('input[type="checkbox"]');
     if (!cb) return;
     const value = (cb.value || '').trim();
     // Skip excluded numeric sizes
     if (!value || seen.has(value) || EXCLUDED_SIZES.has(value)) return;
     seen.add(value);
     const raw = (lab.querySelector('span')?.textContent || value).trim();
     const label = value === 'ONE SIZE' ? 'One Size' : raw;
     out.push({ label, value });
   });
   return out;
 }
 /* ---------- URL helpers ---------- */
 function getUrlSelectedSizes(){
   const p = new URLSearchParams(location.search);
   return p.getAll(URL_KEY_SIZE).map(s => s.trim()).filter(Boolean);
 }
 function setUrlSelectedSizes(values){
   const u = new URL(location.href);
   u.searchParams.delete(URL_KEY_SIZE);
   values.forEach(v => u.searchParams.append(URL_KEY_SIZE, v));
   history.replaceState({}, '', u.toString());
 }
 function removeSizeParamsInPlace(){
   const u = new URL(location.href);
   u.searchParams.delete(URL_KEY_SIZE);
   history.replaceState({}, '', u.toString());
 }
 /* ---------- Pill helpers ---------- */
 function getNextActiveSizePill(){
   return Array.from(
     document.querySelectorAll('#active-filters-list .remove-pill[data-type="checkbox"][data-value]')
   )[0] || null;
 }
 function drainActiveSizePillsLoop(maxSteps = 60, onDone = () => {}){
   let steps = 0, idleFrames = 0;
   function step(){
     if (steps++ > maxSteps) return onDone();
     const pill = getNextActiveSizePill();
     if (pill) {
       idleFrames = 0;
       pill.click();
       requestAnimationFrame(step);
       return;
     }
     idleFrames++;
     if (idleFrames < 2) { requestAnimationFrame(step); return; }
     onDone();
   }
   step();
 }
 /* ---------- UI sync ---------- */
 function syncPressedStates(){
   const root = document.getElementById('br-size-selector');
   if (!root) return;
   const scroll = root.querySelector('.br-size-scroll');
   const clearBtn = root.querySelector('.br-size-clear');
   scroll.querySelectorAll('.br-size-btn').forEach(btn => {
     const v = btn.dataset.value;
     btn.setAttribute('aria-pressed', selected.has(v) ? 'true' : 'false');
   });
   clearBtn.setAttribute('aria-pressed', selected.size === 0 ? 'true' : 'false');
 }
 /* ---------- FAST + fallback clear ---------- */
 function clearAllSizesFastWithFallback(){
   SUPPRESS_SYNC = true;
   // Bulk uncheck
   const cbs = getAllSizeCheckboxes();
   let anyChanged = false;
   cbs.forEach(cb => { if (cb.checked) { cb.checked = false; anyChanged = true; } });
   // One change event
   const facet = getSizeFacet();
   if (anyChanged && facet) {
     facet.dispatchEvent(new Event('change', { bubbles: true }));
   }
   // After 2 frames, verify pills are gone
   let checks = 0;
   function verify() {
     const pill = getNextActiveSizePill();
     if (!pill || checks++ > 1) {
       removeSizeParamsInPlace();
       selected.clear();
       syncPressedStates();
       SUPPRESS_SYNC = false;
       return;
     }
     // fallback: drain slowly
     drainActiveSizePillsLoop(40, () => {
       removeSizeParamsInPlace();
       selected.clear();
       syncPressedStates();
       SUPPRESS_SYNC = false;
     });
   }
   requestAnimationFrame(verify);
 }
 /* ---------- Build / Render ---------- */
 function renderSelector(){
   const root = document.getElementById('br-size-selector');
   if (!root) return;
   const scroll = root.querySelector('.br-size-scroll');
   let clearBtn = root.querySelector('.br-size-clear');
   const sizes = readAvailableSizes();
   if (!sizes.length) return;
   const key = sizes.map(s => s.value).join('|');
   const fromUrl = getUrlSelectedSizes();
   if (!sizesCacheKey) selected = new Set(fromUrl);
   if (key !== sizesCacheKey || !scroll.querySelector('.br-size-track')) {
     sizesCacheKey = key;
     scroll.innerHTML = '';
     const track = document.createElement('div');
     track.className = 'br-size-track';
     scroll.appendChild(track);
     // All Sizes button
     if (!clearBtn) {
       clearBtn = document.createElement('button');
       clearBtn.type = 'button';
       clearBtn.className = 'br-size-clear';
       clearBtn.setAttribute('aria-label','All sizes');
       clearBtn.textContent = 'All Sizes';
     }
     track.appendChild(clearBtn);
     // Size chips
     sizes.forEach(({ label, value }) => {
       const btn = document.createElement('button');
       btn.type = 'button';
       btn.className = 'br-size-btn';
       btn.textContent = label;
       btn.dataset.value = value;
       btn.setAttribute('aria-pressed', selected.has(value) ? 'true' : 'false');
       btn.addEventListener('click', () => {
         if (SUPPRESS_SYNC) return;
         const isOn = btn.getAttribute('aria-pressed') === 'true';
         const nextChecked = !isOn;
         if (nextChecked) selected.add(value); else selected.delete(value);
         SUPPRESS_SYNC = true;
         setCheckboxCheckedByValue(value, nextChecked);
         setUrlSelectedSizes(Array.from(selected));
         btn.setAttribute('aria-pressed', nextChecked ? 'true' : 'false');
         syncPressedStates();
         requestAnimationFrame(() => { SUPPRESS_SYNC = false; });
       });
       track.appendChild(btn);
     });
     // All Sizes click
     if (!clearBtn.dataset.bound) {
       clearBtn.dataset.bound = '1';
       clearBtn.addEventListener('click', (e) => {
         e.preventDefault();
         clearAllSizesFastWithFallback();
       });
     }
     // Bounce hint
     if (!window.__BR_SIZE_BOUNCE_DONE__) {
       const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
       const hasOverflow = scroll.scrollWidth > scroll.clientWidth + 8;
       if (!reduce && hasOverflow) {
         window.__BR_SIZE_BOUNCE_DONE__ = true;
         let count = 0;
         function bounce() {
           scroll.classList.add('is-bounce');
           setTimeout(() => {
             scroll.classList.remove('is-bounce');
             if (++count < 2) setTimeout(bounce, 300);
           }, 700);
         }
         setTimeout(bounce, 150);
       }
     }
   }
   const noneSelected = selected.size === 0;
   root.querySelector('.br-size-clear')?.setAttribute('aria-pressed', noneSelected ? 'true' : 'false');
   syncPressedStates();
 }
 /* ---------- Global syncing ---------- */
 function bindGlobalsOnce(){
   if (globalsBound) return;
   globalsBound = true;
   document.addEventListener('change', (e) => {
     if (SUPPRESS_SYNC) return;
     const cb = e.target;
     if (!(cb instanceof HTMLInputElement) || cb.type !== 'checkbox') return;
     const title = cb.closest('.filters-content__group')
       ?.querySelector('.filters-content__title')?.textContent || '';
     if (!/^\s*size\s*$/i.test(title.trim())) return;
     const v = (cb.value || '').trim();
     if (!v) return;
     if (EXCLUDED_SIZES.has(v)) return; // ignore excluded sizes
     if (cb.checked) selected.add(v); else selected.delete(v);
     setUrlSelectedSizes(Array.from(selected));
     syncPressedStates();
   });
 }
 /* ---------- Init ---------- */
 function init(){
   const tryInit = () => {
     const facet = getSizeFacet();
     const root  = document.getElementById('br-size-selector');
     if (facet && root) { bindGlobalsOnce(); renderSelector(); }
     else setTimeout(tryInit, 150);
   };
   tryInit();
   const container = document.querySelector('.filters-content');
   if (container && !window.__BR_SIZE_OBS__) {
     window.__BR_SIZE_OBS__ = true;
     let raf = null;
     const obs = new MutationObserver(() => {
       if (raf) cancelAnimationFrame(raf);
       raf = requestAnimationFrame(renderSelector);
     });
     obs.observe(container, { childList: true, subtree: true });
   }
 }
 if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
 else init();
})();